import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import { LeadLogContactModal, ScriptPanel } from './LeadContacts';

function pipelineLabel(pipeline) {
  const map = {
    'win_back_non_member':  'Win Back — Non-Member',
    'win_back_past_member': 'Win Back — Past Member',
    'lead_engine':          'Lead Engine',
    'trial_ready':          'Trial Ready',
  };
  return map[pipeline] || pipeline;
}

function ScoreBar({ score }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 70 ? 'var(--red)' : pct >= 45 ? '#f97316' : pct >= 20 ? 'var(--yellow)' : 'var(--text-muted)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--surface-3)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontFamily: 'DM Mono, monospace', minWidth: 24 }}>{score}</span>
    </div>
  );
}

function EligibilityBadge({ eligibility }) {
  if (!eligibility || eligibility === 'eligible') return null;
  const config = {
    review:       { label: 'REVIEW',       bg: 'rgba(234,179,8,0.15)',   color: '#facc15', border: '#facc15' },
    not_eligible: { label: 'NOT ELIGIBLE', bg: 'rgba(107,114,128,0.15)', color: '#9ca3af', border: '#6b7280' },
  };
  const cfg = config[eligibility];
  if (!cfg) return null;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
    }}>{cfg.label}</span>
  );
}

function WinBackCard({ p, expanded, onToggle, onLog, onView, scripts, clubName }) {
  const isPastMember = p.pipeline === 'Win Back Past Member' || p.pipeline === 'win_back_past_member';
  return (
    <div
      style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6, overflow: 'hidden', cursor: 'pointer' }}
      onClick={onToggle}
    >
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3, textTransform: 'capitalize' }}>{p.name}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
            <span style={{
              background: isPastMember ? 'rgba(59,130,246,0.2)' : 'rgba(234,179,8,0.2)',
              color: isPastMember ? '#60a5fa' : 'var(--yellow)',
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            }}>{pipelineLabel(p.pipeline)}</span>
            {p.skillLevel && <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-3)', padding: '1px 6px', borderRadius: 4 }}>{p.skillLevel}</span>}
            <span style={{
              background: isPastMember ? 'rgba(234,88,12,0.2)' : 'rgba(59,130,246,0.2)',
              color: isPastMember ? '#f97316' : '#60a5fa',
              border: `1px solid ${isPastMember ? '#f97316' : '#60a5fa'}`,
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            }}>{isPastMember ? '📞 CALL FIRST' : '💬 TEXT FIRST'}</span>
            <EligibilityBadge eligibility={p.trialEligibility} />
          </div>
          {p.email && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{p.email}{p.phone && <span> · {p.phone}</span>}</div>}
          {(p.originTag === 'Past Trial' || p.isPastMember || p.visits30d > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
              {p.originTag === 'Past Trial' && (
                <span style={{ background: 'rgba(168,85,247,0.10)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                  💜 Past Trial
                </span>
              )}
              {p.isPastMember && (
                <span style={{ background: 'rgba(59,130,246,0.10)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                  {(() => {
                    if (!p.memberLapsedDate) return `💙 Past Member${p.membershipType ? ' · ' + p.membershipType : ''}`;
                    const days = Math.floor((new Date() - new Date(p.memberLapsedDate)) / 86400000);
                    return days <= 0 ? `💙 Past Member${p.membershipType ? ' · ' + p.membershipType : ''}` : `💙 Past Member · ${days}d ago`;
                  })()}
                </span>
              )}
              {p.visits30d > 0 && (
                <span style={{
                  background: p.visits30d >= 4 ? '#22c55e22' : p.visits30d >= 2 ? '#eab30822' : '#6b728022',
                  color: p.visits30d >= 4 ? '#22c55e' : p.visits30d >= 2 ? '#eab308' : '#9ca3af',
                  border: `1px solid ${p.visits30d >= 4 ? '#22c55e44' : p.visits30d >= 2 ? '#eab30844' : '#6b728044'}`,
                  borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700,
                }}>{p.visits30d}x last 30d</span>
              )}
            </div>
          )}
          <ScoreBar score={p.score} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{expanded ? '▴' : '▾'}</div>
      </div>
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
          {p.email && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>✉ {p.email}</div>}
          {p.phone && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>📞 {p.phone}</div>}
          {scripts && <ScriptPanel prospect={p} scripts={scripts} role={null} userName={null} clubName={clubName || ''} />}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => onLog(p)}>Log Contact</button>
            <button style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 5, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
              onClick={() => onView(p.id)}>View Record</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadWinBack({ onSelectProspect }) {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [scripts, setScripts] = useState([]);

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    get('/scripts?module=lead_engine').then(res => setScripts(res?.scripts || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/lead/win-back?club=${currentClub.id}`)
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (error)   return <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>Failed to load — {error}</div>;

  const completedIds = new Set(completed.map(c => c.id));
  const prospects = (data?.prospects || []).filter(p => {
    if (completedIds.has(p.id)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s) || p.pipeline?.toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Win Back</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', fontFamily: 'DM Mono, monospace' }}>{data?.total || 0}</div>
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <input className="field-input" type="text" placeholder="Search by name, email, pipeline..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%' }} />
      </div>
      {prospects.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-text">No win back prospects.</div></div>
      ) : (
        prospects.map(p => (
          <WinBackCard
            key={p.id} p={p}
            expanded={expandedId === p.id}
            onToggle={() => setExpandedId(prev => prev === p.id ? null : p.id)}
            onLog={prospect => { setExpandedId(null); setOutcomeTarget(prospect); }}
            onView={onSelectProspect}
            scripts={scripts}
            clubName={currentClub?.name}
          />
        ))
      )}
      {outcomeTarget && (
        <LeadLogContactModal
          prospect={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={(outcome) => {
            setCompleted(prev => [...prev, { ...outcomeTarget, outcome }]);
            setOutcomeTarget(null);
            load();
          }}
          scripts={scripts}
          clubName={currentClub?.name}
        />
      )}
    </div>
  );
}
